import { useEffect, useRef, useState } from 'react';
import { api, getPinUnlockToken } from '../lib/api';
import { Lock, X, Loader2, ShieldCheck } from 'lucide-react';
import PinGateModal from './PinGateModal';

/**
 * ChildPinModal — Iter-51.35
 *
 * Shown when a child tile is tapped in /profiles AND that tile has a
 * PIN set. Enforces the sibling privacy gate: no navigation until the
 * correct 4-8 char PIN is entered.
 *
 * Props:
 *   child       — the child object being unlocked (name, avatar_url, child_id)
 *   onUnlocked  — (token) => void — called with the server-issued unlock
 *                 token after a correct PIN OR a parent override.
 *   onCancel    — user tapped X / clicked the backdrop; ChildPinModal closes.
 *
 * Layout mirrors PinGateModal so the two feel like the same design
 * system. Also embeds a "Parent/Guardian override" flow — a small
 * secondary button that opens the standard Parent PinGateModal, then
 * hits /pin/override to get a same-shape unlock token.
 */
export default function ChildPinModal({ child, onUnlocked, onCancel }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // When the parent taps "Parent/Guardian override →" we open the
  // existing Parent-PIN gate; on success we call the override endpoint
  // to get a same-shape unlock token.
  const [parentGateOpen, setParentGateOpen] = useState(false);
  const [overrideMode, setOverrideMode] = useState(null); // 'let-in' | 'reset'
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Poll cooldown every second so the timer counts down live.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const trySubmit = async () => {
    if (busy) return;
    setError('');
    if (pin.length < 4) {
      setError('Enter your PIN');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/children/${child.child_id}/pin/verify`, { pin });
      if (data.ok) {
        onUnlocked(data.unlock_token);
      } else if (data.reason === 'locked') {
        setCooldown(data.locked_until_seconds || 30);
        setError(`Too many tries — try again in ${data.locked_until_seconds || 30}s`);
        setPin('');
      } else {
        setError(`That PIN didn't work — try again${
          data.locked_until_seconds ? ` (locked ${data.locked_until_seconds}s)` : ''
        }`);
        if (data.locked_until_seconds) setCooldown(data.locked_until_seconds);
        setPin('');
      }
    } catch (e) {
      setError('Network error — try again');
    } finally {
      setBusy(false);
    }
  };

  // Parent PIN unlocked — now hit /pin/override (let-in) or open reset flow.
  // PinGateModal stashes the unlock token in sessionStorage on success
  // and calls onUnlocked() without arguments; we read it back here.
  const onParentPinOk = async () => {
    setParentGateOpen(false);
    const parentToken = getPinUnlockToken();
    if (!parentToken) { setError('Parent unlock failed'); return; }
    if (overrideMode === 'let-in') {
      try {
        const { data } = await api.post(
          `/children/${child.child_id}/pin/override`,
          {},
          { headers: { 'x-pin-unlock': parentToken } },
        );
        if (data.ok) onUnlocked(data.unlock_token);
      } catch {
        setError('Override failed — try again');
      }
    } else if (overrideMode === 'reset') {
      // Kick the parent into the profile settings PIN card by unlocking
      // and closing the modal — the parent then heads to Profile → Change PIN.
      // Simplest handoff without adding a new full-screen modal.
      try {
        const { data } = await api.post(
          `/children/${child.child_id}/pin/override`,
          {},
          { headers: { 'x-pin-unlock': parentToken } },
        );
        if (data.ok) {
          onUnlocked(data.unlock_token);
          // Deep-hint to the parent so they know where to go next.
          try {
            sessionStorage.setItem('goToChildPinSettings', child.child_id);
          } catch {}
        }
      } catch {
        setError('Override failed — try again');
      }
    }
    setOverrideMode(null);
  };

  return (
    <>
      <div
        data-testid="child-pin-modal"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onCancel}
      >
        <div
          className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-[#5B8DEF] flex items-center justify-center flex-shrink-0">
              <Lock size={22} strokeWidth={2.5} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-extrabold text-[#2B2140] truncate">
                Enter {child.name || 'this profile'}'s PIN
              </div>
              <div className="text-xs text-slate-500 leading-relaxed">
                Ask your parent or guardian if you don't know it.
              </div>
            </div>
            <button
              data-testid="child-pin-cancel"
              onClick={onCancel}
              aria-label="Cancel"
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0"
            >
              <X size={16} strokeWidth={3} />
            </button>
          </div>

          {/* Avatar + child name pill for visual confirmation. */}
          <div className="flex items-center justify-center gap-3 my-3">
            <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-white shadow-sm overflow-hidden flex items-center justify-center">
              {child.avatar_url ? (
                <img src={child.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-extrabold text-slate-400">
                  {(child.name || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div className="text-base font-extrabold text-[#2B2140]">{child.name}</div>
          </div>

          <input
            data-testid="child-pin-input"
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\s/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && trySubmit()}
            disabled={busy || cooldown > 0}
            placeholder="• • • •"
            className={`w-full text-center text-3xl tracking-[0.6em] font-extrabold py-3 rounded-2xl border-2 outline-none transition-all ${
              error ? 'border-rose-400 bg-rose-50 animate-shake' : 'border-slate-200 bg-slate-50 focus:border-[#5B8DEF]'
            }`}
          />

          {error && (
            <div data-testid="child-pin-error" className="mt-2 text-xs font-bold text-rose-600 text-center">
              {error}
            </div>
          )}
          {cooldown > 0 && (
            <div data-testid="child-pin-cooldown" className="mt-1 text-[11px] text-slate-500 text-center">
              Cool-off timer: {cooldown}s
            </div>
          )}

          <button
            data-testid="child-pin-submit"
            onClick={trySubmit}
            disabled={busy || cooldown > 0}
            className="mt-3 w-full bg-[#FF6B1A] hover:bg-[#E85E15] disabled:opacity-50 text-white font-extrabold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} strokeWidth={3} />}
            Unlock profile
          </button>

          {/* Parent/Guardian override — smaller secondary CTA below. */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-1.5">
            <button
              data-testid="child-pin-parent-letin"
              onClick={() => { setOverrideMode('let-in'); setParentGateOpen(true); }}
              className="w-full text-xs font-extrabold text-[#5B8DEF] hover:underline inline-flex items-center justify-center gap-1"
            >
              <ShieldCheck size={12} strokeWidth={3} />
              Parent / Guardian — just let me in this time
            </button>
            <button
              data-testid="child-pin-parent-reset"
              onClick={() => { setOverrideMode('reset'); setParentGateOpen(true); }}
              className="w-full text-xs font-bold text-slate-500 hover:text-[#B85582] hover:underline"
            >
              Forgot the child PIN? Reset it via Parent PIN
            </button>
          </div>
        </div>
      </div>

      {parentGateOpen && (
        <PinGateModal
          context="child-override"
          onUnlocked={onParentPinOk}
          onCancel={() => { setParentGateOpen(false); setOverrideMode(null); }}
        />
      )}
    </>
  );
}
